/**
 * 日落橙模版 - 导航栏
 * 
 * 设计理念：温暖琥珀色调，介于teal冷色和red暖色之间
 * - 顶部 amber→orange→rose 渐变品牌色条
 * - 半透明玻璃态导航栏，柔和圆形阴影
 * - 更圆润的按钮和图标风格
 * - 暖白背景，琥珀焦点色
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function SunsetNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const { siteId, loading: siteLoading, siteName: contextSiteName } = useSite();
  const { siteConfig, navMenus, navCategories } = useSiteConfig();
  const enableVip = siteConfig?.enable_vip ?? true;

  const siteName = contextSiteName;

  useEffect(() => {
    if (!siteConfig) return;
    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate("/");
  };

  const isHome = location.pathname === "/";

  return (
    <>
      {/* 顶部品牌色条 */}
      <div className="h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500" />

      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-[0_4px_20px_-6px_rgba(245,158,11,0.12)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[4.2rem]">

            {/* ── 左侧：Logo + 站点名 ── */}
            <Link to="/" className="flex items-center gap-3 shrink-0 group">
              {logo && !logoError ? (
                <img
                  src={logo}
                  alt={siteName}
                  className="h-10 w-auto object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 flex items-center justify-center text-white font-bold text-base shadow-lg shadow-amber-500/30">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'S'}
                </div>
              )}
              {siteName && (
                <span className="text-lg font-bold text-gray-800 tracking-tight group-hover:text-amber-600 transition-colors">
                  {siteName}
                </span>
              )}
            </Link>

            {/* ── 中间：主导航（桌面端）── */}
            <div className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2.5 text-[15px] font-medium rounded-xl transition-all ${
                  isHome
                    ? "text-amber-600 bg-amber-50"
                    : "text-gray-600 hover:text-amber-600 hover:bg-amber-50/50"
                }`}
              >
                首页
              </Link>
              {navMenus.map((menu) => (
                <Link
                  key={menu.id}
                  to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                       menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                       menu.type === 'link' ? (menu.target_url || '#') : '#'}
                  className="px-4 py-2.5 text-[15px] font-medium rounded-xl text-gray-600 hover:text-amber-600 hover:bg-amber-50/50 transition-all"
                >
                  {menu.name}
                </Link>
              ))}
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2.5 text-[15px] font-medium rounded-xl transition-all ${
                    location.pathname === `/category/${cat.slug}`
                      ? "text-amber-600 bg-amber-50"
                      : "text-gray-600 hover:text-amber-600 hover:bg-amber-50/50"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* ── 右侧：用户区 + 发布按钮 ── */}
            <div className="flex items-center gap-3">
              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-amber-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden shadow-sm">
                      {user?.avatar ? (
                        <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (user?.nickname || 'U').charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="text-sm text-gray-600 hidden md:inline max-w-[80px] truncate">
                      {user?.nickname}
                    </span>
                    <i className="ri-arrow-down-s-line text-gray-400 text-xs" />
                  </button>

                  {isUserMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsUserMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-xl shadow-amber-500/5 border border-gray-100 py-2 z-20">
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-user-line text-base" />会员中心
                        </Link>
                        <Link
                          to="/dashboard/my-listings"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-file-list-line text-base" />我的发布
                        </Link>
                        {enableVip && (
                          <Link
                            to="/vip"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-amber-600 font-medium hover:bg-amber-50 transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <i className="ri-vip-crown-line text-base" />开通VIP
                          </Link>
                        )}
                        {isAdmin && (
                          <>
                            <div className="border-t border-gray-100 my-1" />
                            <Link
                              to="/admin"
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <i className="ri-shield-star-line text-base" />后台管理
                            </Link>
                          </>
                        )}
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50 hover:text-red-500 transition-colors w-full text-left"
                        >
                          <i className="ri-logout-box-line text-base" />退出登录
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => navigate("/login")}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-600 rounded-xl hover:bg-amber-50 hover:text-amber-600 transition-colors"
                >
                  <i className="ri-user-line" />
                  <span>登录</span>
                </button>
              )}

              <button
                onClick={() => navigate("/publish")}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30"
              >
                <i className="ri-add-line text-base" />
                <span className="hidden sm:inline">发布信息</span>
              </button>

              {/* 移动端菜单按钮 */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-xl hover:bg-amber-50 transition-colors"
              >
                <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-xl text-gray-600`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── 移动端菜单 ── */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-amber-100 bg-white shadow-lg">
            <div className="px-4 py-3 space-y-1 max-h-[60vh] overflow-y-auto">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 text-[15px] font-medium rounded-xl transition-colors ${
                  isHome ? "text-amber-600 bg-amber-50" : "text-gray-700 hover:bg-amber-50"
                }`}
              >
                <i className="ri-home-4-line text-lg" />首页
              </Link>
              {navMenus.map((menu) => (
                <Link
                  key={menu.id}
                  to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                       menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                       menu.type === 'link' ? (menu.target_url || '#') : '#'}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-700 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <i className="ri-menu-line text-lg" />{menu.name}
                </Link>
              ))}
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-[15px] font-medium rounded-xl transition-colors ${
                    location.pathname === `/category/${cat.slug}` ? "text-amber-600 bg-amber-50" : "text-gray-700 hover:bg-amber-50"
                  }`}
                >
                  <i className="ri-grid-line text-lg" />{cat.name}
                </Link>
              ))}

              {enableVip && (
                <Link
                  to="/vip"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-amber-600 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <i className="ri-vip-crown-line text-lg" />开通VIP
                </Link>
              )}

              <div className="border-t border-amber-100 my-2" />

              {isLoggedIn ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-700 rounded-xl hover:bg-amber-50 transition-colors">
                    <i className="ri-user-line text-lg" />会员中心
                  </Link>
                  <Link to="/dashboard/my-listings" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-700 rounded-xl hover:bg-amber-50 transition-colors">
                    <i className="ri-file-list-line text-lg" />我的发布
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-700 rounded-xl hover:bg-amber-50 transition-colors">
                      <i className="ri-shield-star-line text-lg" />后台管理
                    </Link>
                  )}
                  <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-500 rounded-xl hover:bg-gray-50 transition-colors w-full">
                    <i className="ri-logout-box-line text-lg" />退出登录
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-amber-600 rounded-xl hover:bg-amber-50 transition-colors">
                  <i className="ri-user-line text-lg" />登录 / 注册
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
